import { useCallback, useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog, F0Select } from '@factorialco/f0-react'
import { AutoGrid } from '@factorialco/f0-react/dist/experimental'
import { Image, Megaphone, Phone, Settings, Video } from '@factorialco/f0-react/icons/app'
import { useMediaDeviceSelect, usePersistentUserChoices } from '@livekit/components-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  VIRTUAL_BACKGROUND_TYPES,
  getVirtualBackgroundOptions,
} from 'modules/meetings/lib/constants/VirtualBackground'
import { useVirtualBackgroundSettings } from 'modules/meetings/lib/hooks/useVirtualBackgroundSettings'
import { isVirtualBackgroundSupported } from 'modules/meetings/lib/utils/virtualBackgroundUtils'

interface PrejoinSettingsButtonProps {
  onVideoDeviceIdChange: (deviceId: string) => void
  onBackgroundTypeChange: (backgroundType: string) => void
}
export const PrejoinSettingsButton = ({
  onVideoDeviceIdChange,
  onBackgroundTypeChange,
}: PrejoinSettingsButtonProps) => {
  const { userChoices, saveAudioInputDeviceId, saveVideoInputDeviceId } = usePersistentUserChoices()
  const [openModal, setOpenModal] = useState(false)
  const [backgroundSupported, setBackgroundSupported] = useState(false)
  const { backgroundType, setBackgroundType } = useVirtualBackgroundSettings()

  const { devices: audioInputDevices } = useMediaDeviceSelect({ kind: 'audioinput' })
  const { devices: videoInputDevices } = useMediaDeviceSelect({ kind: 'videoinput' })

  const {
    devices: audioOutputDevices,
    activeDeviceId: activeAudioOutputId,
    setActiveMediaDevice: setActiveAudioOutputDevice,
  } = useMediaDeviceSelect({ kind: 'audiooutput' })

  const isAudioOutputSelectionSupported = 'setSinkId' in HTMLMediaElement.prototype

  // Load saved audio output device from localStorage on mount
  useEffect(() => {
    const savedAudioOutputDeviceId = localStorage.getItem('livekit-audio-output-device')
    if (savedAudioOutputDeviceId && setActiveAudioOutputDevice) {
      setActiveAudioOutputDevice(savedAudioOutputDeviceId)
    }
  }, [setActiveAudioOutputDevice])

  // Check blur support on mount
  useEffect(() => {
    isVirtualBackgroundSupported().then((supported) => setBackgroundSupported(supported))
  }, [])

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      if (isAudioOutputSelectionSupported && setActiveAudioOutputDevice) {
        await setActiveAudioOutputDevice(deviceId)
        // Save to localStorage for persistence across sessions
        localStorage.setItem('livekit-audio-output-device', deviceId)
      }
    },
    [isAudioOutputSelectionSupported, setActiveAudioOutputDevice]
  )

  const handleBackgroundChange = useCallback(
    (value: string) => {
      setBackgroundType(value)
      onBackgroundTypeChange(value)
    },
    [setBackgroundType, onBackgroundTypeChange]
  )

  return (
    <>
      <F0Button
        label={i18n.t('meetings.prejoin.control_bar.settings')}
        onClick={() => {
          setOpenModal(true)
          track(trackingEvents.meetings.ControlBarSettingsButton)
        }}
        icon={Settings}
        hideLabel
        variant="outline"
        size="lg"
      />

      <F0Dialog
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={i18n.t('meetings.room.settings.title')}
        width="sm"
      >
        <Box flexDirection="column" gap="s20">
          <Text.Normal size="300" weight="medium" color="black">
            {i18n.t('meetings.room.settings.general_title')}
          </Text.Normal>
          <AutoGrid gap="4" tileSize="sm">
            <F0Select
              onChange={(deviceId: string) => {
                track(trackingEvents.meetings.SettingsModalAudioInputChange, {
                  deviceId,
                })
                saveAudioInputDeviceId(deviceId)
              }}
              options={audioInputDevices.map((device) => ({
                label:
                  device.label ||
                  `${i18n.t(
                    'meetings.room.control_bar.microphone.label'
                  )} ${device.deviceId.substring(0, 5)}...`,
                value: device.deviceId,
              }))}
              placeholder={i18n.t('meetings.room.control_bar.microphone.placeholder')}
              label={i18n.t('meetings.room.settings.audio_input')}
              searchBoxPlaceholder={i18n.t(
                'meetings.room.control_bar.microphone.search_placeholder'
              )}
              searchEmptyMessage={i18n.t(
                'meetings.room.control_bar.microphone.no_microphones_found'
              )}
              value={userChoices.audioDeviceId || ''}
              icon={Megaphone}
            />

            {isAudioOutputSelectionSupported && (
              <F0Select
                onChange={(deviceId: string) => {
                  track(trackingEvents.meetings.SettingsModalAudioOutputChange, {
                    deviceId,
                  })
                  handleAudioOutputChange(deviceId)
                }}
                options={audioOutputDevices.map((device) => ({
                  label:
                    device.label ||
                    `${i18n.t(
                      'meetings.room.control_bar.speakers.label'
                    )} ${device.deviceId.substring(0, 5)}...`,
                  value: device.deviceId,
                }))}
                placeholder={i18n.t('meetings.room.control_bar.speakers.placeholder')}
                label={i18n.t('meetings.room.settings.audio_output')}
                searchBoxPlaceholder={i18n.t(
                  'meetings.room.control_bar.speakers.search_placeholder'
                )}
                searchEmptyMessage={i18n.t('meetings.room.control_bar.speakers.no_speakers_found')}
                value={activeAudioOutputId || ''}
                icon={Phone}
              />
            )}

            <F0Select
              label={i18n.t('meetings.room.settings.video_input')}
              options={videoInputDevices.map((device) => ({
                label:
                  device.label ||
                  `${i18n.t('meetings.room.control_bar.camera.label')} ${device.deviceId.substring(
                    0,
                    5
                  )}...`,
                value: device.deviceId,
              }))}
              onChange={(deviceId: string) => {
                track(trackingEvents.meetings.SettingsModalVideoInputChange, {
                  deviceId,
                })
                saveVideoInputDeviceId(deviceId)
                onVideoDeviceIdChange(deviceId)
              }}
              placeholder={i18n.t('meetings.room.control_bar.camera.placeholder')}
              searchBoxPlaceholder={i18n.t('meetings.room.control_bar.camera.search_placeholder')}
              searchEmptyMessage={i18n.t('meetings.room.control_bar.camera.no_cameras_found')}
              value={userChoices.videoDeviceId || ''}
              icon={Video}
            />
          </AutoGrid>
          <Text.Normal size="300" weight="medium" color="black">
            {i18n.t('meetings.room.settings.effects_title')}
          </Text.Normal>
          <AutoGrid gap="4" tileSize="sm">
            {backgroundSupported && (
              <F0Select
                label={i18n.t('meetings.room.settings.blur_background.label')}
                options={getVirtualBackgroundOptions()}
                placeholder={i18n.t('meetings.room.settings.blur_background.placeholder')}
                searchBoxPlaceholder={i18n.t(
                  'meetings.room.settings.blur_background.search_placeholder'
                )}
                searchEmptyMessage={i18n.t(
                  'meetings.room.settings.blur_background.no_backgrounds_found'
                )}
                value={backgroundType || VIRTUAL_BACKGROUND_TYPES.DISABLED}
                icon={Image}
                onChange={(value: string) => {
                  track(trackingEvents.meetings.SettingsModalBackgroundChange, {
                    value,
                  })
                  handleBackgroundChange(value)
                }}
              />
            )}
          </AutoGrid>
        </Box>
      </F0Dialog>
    </>
  )
}
